import defaultAxios from "axios";
import { apiURL } from "./apiURL";
import { storage } from "./storage";
import { ERROR_CODES } from "../lib/constants/error";
import routeConstant from "../lib/constants/routeConstant";
// This file handles making HTTP POST requests to a specified API endpoint using Axios. Sends a POST request to the given URL with the provided data.

const tempObj = {
  data: {
    status: -1,
    message: "Something went wrong. Please try again later.",
  },
};

// Creating our own Custom Axios Instance
const axios = new defaultAxios.create({
  baseURL: apiURL, // Base URL of the API
  headers: {
    Accept: "application/json",
  },
});

// Axios request interceptor to add the authorization token to every request
axios.interceptors.request.use((config) => {
  const token = storage.fetchFromLocalStorage.token(); // fetch token
  const session = storage.fetchFromSessionStorage.sessionKey(); // fetch session-Key
  const universityId = storage.fetchFromLocalStorage.universityId() // fetch universityId

  // Add the token to the request headers only if it exists(or is Logined.)
  if (token) {
    config.headers.Authorization = `${token}`;
  }

  // If request method is POST or PUT (means when we have data to pass)
  // Add the sessionKey to the request headers only if it exists
  if (config.method === 'post' || config.method === 'put') {
    if (!universityId) {
      config.data = {
        ...config.data, // Preserve existing data
        sessionKey: session,
        // subDomain: getSubdomain(window.location.hostname)
      };
    } else {
      config.data = {
        ...config.data, // Preserve existing data
        sessionKey: session,
        universityId: universityId ? universityId : null,
        // subDomain: getSubdomain(window.location.hostname)
      }
    };
  }

  return config;
});

// Axios response interceptor to handle responses and errors globally
axios.interceptors.response.use(
  (response) => {
    if (response.data.code === ERROR_CODES.AUTHENTICATION_ERROR) {
      // TODO: Logout User
      // Handle this for Authentication error/ Access Denied
      storage.destroyFromSessionStorage.sessionKey(); // fetch session-key
      storage.destroyFromLocalStorage.token(); // fetch token
      storage.destroyFromLocalStorage.user();

      // refresh-page
      window.location.href = routeConstant.DEFAULT_PAGE;
    }

    // if(response.data.code === ERROR_CODES.UNIVERSITY_DETAILS_NOT_PROVIDED || response.data.code === ERROR_CODES.UNIVERSITY_NOT_FOUND){
    //   // Reload Page and Clear universityId from localStorage
    //   storage.destroyFromLocalStorage.universityId();
    //   window.location.reload();
    // }

    // If we have token in the response
    if (response.data.data && response.data.data.token) {
      if (response.data.data.token !== storage.fetchFromLocalStorage.token()) {
        storage.setInLocalStorage.token(response.data.data.token);
      }
    }

    // If we have updatedUniversityId in the response
    if (response.data.data && response.data.data.updatedUniversityId) {
      storage.setInLocalStorage.universityId(response.data.data.updatedUniversityId);
    }

    // If we have sessionKey in the response
    if (response.data.data && response.data.data.sessionKey) {
      if (response.data.data.sessionKey !== storage.fetchFromSessionStorage.sessionKey()) {
        storage.setInSessionStorage.sessionKey(response.data.data.sessionKey);
      }
    }

    return response;
  },
  (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        console.error("Unauthorized! Redirecting to login...");
      }
    } else {
      console.error("Network Error:", error);
    }

    return Promise.reject(error);
  }
);

/**
 * Posts a request to the given url with the given data.
 * @param {string} url The URL to post the request to.
 * @param {object} data The data to post.
 * @returns {object} The response from the server.
 */
const PostRequest = async (url, data) => {
  try {
    const res = await axios.post(url, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return res ? res : tempObj;
  } catch (err) {
    console.log(err);
    return {
      data: {
        ...tempObj.data,
        message: err?.response?.data?.message ? err?.response?.data?.message : tempObj.data.message,
      },
    };
  }
};

/**
 * Posts a file request to the given url with the given data.
 * @param {string} url The URL to post the request to.
 * @param {object} data The data to post. This must be a FormData object.
 * @returns {object} The response from the server.
 */
const PostFileRequest = async (url, data, onProgress) => {
  try {
    const res = await axios.post(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          onProgress(progressEvent);
        }
      }
    }); // This handles the progress events
    return res ? res : tempObj;
  } catch (err) {
    console.log(err);
    return {
      data: {
        ...tempObj.data,
        message: err?.response?.data?.message ? err?.response?.data?.message : tempObj.data.message,
      },
    };
  }
};

const PostMultiPartDataRequest = async (url, data) => {
  try {
    const res = await axios.post(url, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res ? res : tempObj;
  } catch (err) {
    console.log(err);
    return {
      data: {
        ...tempObj.data,
        message: err?.response?.data?.message ? err?.response?.data?.message : tempObj.data.message,
      },
    };
  }
};

export const Api = {
  PostRequest,
  PostFileRequest,
  PostMultiPartDataRequest
};
